import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { connectorsAPI, ConnectorRegistryEntry } from '../../services/api';
import { readProblem, isSafeAuthorizeUrl } from './connectorErrors';
import { leavePageFor } from './leavePage';
import { messageForCode, retryInCopy } from './copy';

/**
 * useConnectorConnect — Connect / Reconnect for a bring-your-own connector.
 *
 * POST /api/connectors/:id/connect with returnTo = the current path and
 * query, then a full-page move to the service's consent page (no popup; the
 * service sends the browser back to /connectors/callback). The button stays
 * disabled from the click until the page leaves. A 429 disables it for the
 * Retry-After seconds, counting down; any other failure shows the copy for
 * its code. GitHub connects on the spot (the signed-in GitHub link): no page
 * change, and `onConnected` refreshes whatever shows the connection.
 */
export interface ConnectorConnectState {
  connect: (entry: ConnectorRegistryEntry) => Promise<void>;
  /** The connector whose consent link is being fetched (or being left for). */
  pendingId: string | null;
  /** A line to show under that connector's button, or null. */
  messageFor: (id: string) => string | null;
  /** True while that connector cannot be clicked (in flight or rate limited). */
  isBlocked: (id: string) => boolean;
}

export interface ConnectedInPlace {
  connectorId: string;
  accountLabel: string | null;
  accountSwitched: boolean;
}

export function useConnectorConnect(
  opts: { onConnected?: (result: ConnectedInPlace) => void | Promise<void> } = {},
): ConnectorConnectState {
  const onConnectedRef = useRef(opts.onConnected);
  onConnectedRef.current = opts.onConnected;
  const location = useLocation();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ id: string; text: string } | null>(null);
  const [retry, setRetry] = useState<{ id: string; until: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Back from the consent page via the browser's cache: the page is restored
  // as it was left, mid-connect. Make the button usable again.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      inFlightRef.current = false;
      setPendingId(null);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  // Tick once a second while a retry wait runs, so the seconds count down.
  useEffect(() => {
    if (!retry) return undefined;
    const t = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= retry.until) setRetry(null);
    }, 1000);
    return () => clearInterval(t);
  }, [retry]);

  const connect = useCallback(
    async (entry: ConnectorRegistryEntry) => {
      if (inFlightRef.current) return;
      if (retry && retry.id === entry.id && Date.now() < retry.until) return;
      inFlightRef.current = true;
      setPendingId(entry.id);
      setFailure(null);
      try {
        const returnTo = `${location.pathname}${location.search}`;
        const res = await connectorsAPI.connect(entry.id, returnTo);
        if (res && res.data && res.data.connected) {
          inFlightRef.current = false;
          if (!mountedRef.current) return;
          setPendingId(null);
          const done = onConnectedRef.current;
          if (done) {
            await done({
              connectorId: entry.id,
              accountLabel: res.data.accountLabel ?? null,
              accountSwitched: !!res.data.accountSwitched,
            });
          }
          return;
        }
        const url = res && res.data && res.data.authorizeUrl;
        if (!isSafeAuthorizeUrl(url)) throw new Error('unsafe consent link');
        // Leaving the page: keep the button disabled until it is gone.
        leavePageFor(url);
      } catch (err) {
        inFlightRef.current = false;
        if (!mountedRef.current) return;
        setPendingId(null);
        const p = readProblem(err);
        if (p.status === 429 || p.code === 'rate_limited') {
          const seconds = p.retryAfterSeconds ?? 60;
          const n = Date.now();
          setNow(n);
          setRetry({ id: entry.id, until: n + seconds * 1000 });
          return;
        }
        setFailure({ id: entry.id, text: messageForCode(p.code, entry.displayName) });
      }
    },
    [location.pathname, location.search, retry],
  );

  const messageFor = useCallback(
    (id: string) => {
      if (retry && retry.id === id && now < retry.until) return retryInCopy((retry.until - now) / 1000);
      if (failure && failure.id === id) return failure.text;
      return null;
    },
    [retry, failure, now],
  );

  const isBlocked = useCallback(
    (id: string) => pendingId === id || !!(retry && retry.id === id && now < retry.until),
    [pendingId, retry, now],
  );

  return { connect, pendingId, messageFor, isBlocked };
}
